import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Loader } from '@squirrellearningltd/go-teacher-components';
import { APPS, getUrl } from '@squirrellearningltd/go-shared-utils';
import { Link } from '@reach/router';
import { Menu } from 'react-feather';
import AddBoomerItem from './AddBoomerItem';
import categoryStyles from '../../components/ListCategory/ListCategories.module.scss';

import BackLink from '../../components/BackLink';
import Tab from '../../components/Tab';
import Tabs from '../../components/Tabs';
import boomerItemsImage from '../../images/boomer_admin_items.png';
import plusCircled from '../../images/plus-circled.svg';
import { getBoomerItemCategory } from './categories';
import styles from './BoomerItems.module.scss';
import { ARCHIVE_UNARCHIVE_AVATAR_ITEM, DELETE_AVATAR_ITEM, GET_BOOMER_ITEMS } from './services/graphql';

const TABS = {
    published: 1,
    draft: 2,
    archived: 3,
};

const getItemImage = (item) => {
    const itemUrl = Array.isArray(item.url) ? item.url[0] : item.url;

    if (!itemUrl || /^(https?:)?\/\//.test(itemUrl) || itemUrl.startsWith('data:')) {
        return itemUrl;
    }

    return `${getUrl(APPS.go_portal_pupil)}${
        itemUrl.startsWith('/') ? itemUrl : `/${itemUrl}`
    }`;
};

const BoomerCategoryItems = ({ categoryKey, itemId, location }) => {
    const [selectedTab, setSelectedTab] = useState(location?.state?.draft ? TABS.draft : TABS.published);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [actionError, setActionError] = useState('');
    const [archiveAvatarItem, { loading: archiving }] = useMutation(ARCHIVE_UNARCHIVE_AVATAR_ITEM);
    const [deleteAvatarItem, { loading: deleting }] = useMutation(DELETE_AVATAR_ITEM);
    const menuRef = useRef(null);
    useEffect(() => {
        const closeMenu = event => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setOpenMenuId(null);
        };
        const onKeyDown = event => {
            if (event.key === 'Escape') setOpenMenuId(null);
        };
        document.addEventListener('click', closeMenu);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('click', closeMenu);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);
    const category = getBoomerItemCategory(categoryKey);
    const showingArchived = !itemId && selectedTab === TABS.archived;
    const queryVariables = { published: !itemId && selectedTab === TABS.published };
    if (showingArchived) {
        queryVariables.archived = true;
    }

    const {
        data: itemsData,
        error: itemsError,
        loading: itemsLoading,
        refetch,
    } = useQuery(GET_BOOMER_ITEMS, {
        skip: !category,
        variables: queryVariables,
        fetchPolicy: 'network-only',
    });

    const onArchiveItem = (id, archived) => {
        if (archiving || deleting) return;
        setActionError('');
        archiveAvatarItem({ variables: { avatarItemId: id, archived, published: !archived } }).then(({ data }) => {
            const result = data && data.archiveUnarchiveAvatarItem;
            if (!result || !result.avatarItem || (result.errors && result.errors.length)) {
                setActionError(result && result.errors && result.errors.length ? result.errors.join(', ') : 'Could not update item.');
                return;
            }
            setOpenMenuId(null);
            return refetch();
        }).catch(error => setActionError(error.message));
    };

    const onDeleteItem = (id) => {
        if (deleting || archiving) return;
        setActionError('');
        deleteAvatarItem({ variables: { avatarItemId: id } }).then(({ data }) => {
            const result = data && data.deleteAvatarItem;
            if (!result || !result.deleted || (result.errors && result.errors.length)) {
                setActionError(result && result.errors && result.errors.length ? result.errors.join(', ') : 'Could not delete item.');
                return;
            }
            setOpenMenuId(null);
            return refetch();
        }).catch(error => setActionError(error.message));
    };

    if (!category) {
        return (
            <>
                <div className={styles.sectionHeader}>
                    <h1 className={styles.headerTitle}>Boomer Items</h1>
                    <BackLink to='/boomerItems' text='Back' />
                </div>
                <p className={styles.statusMessage}>Category not found.</p>
            </>
        );
    }

    const allItems = itemsData?.avatarItems || [];
    const categoryItems = allItems.filter(
        (item) => item.type === category.type
    );
    if (itemId) {
        if (itemsLoading) return <Loader />;
        if (itemsError) return <p role='alert'>{itemsError.message}</p>;
        const item = categoryItems.find(item => item.id === decodeURIComponent(itemId) && item.published !== true);
        return item ? <AddBoomerItem key={item.id} categoryKey={categoryKey} item={item} /> : (
            <><BackLink to={`/boomerItems/${categoryKey}`} text='Back' /><p>Draft item not found.</p></>
        );
    }
    const visibleItems = showingArchived ? categoryItems : categoryItems.filter((item) =>
        selectedTab === TABS.published
            ? item.published === true
            : item.published !== true
    );
    const selectedTabName =
        showingArchived ? 'archived' : selectedTab === TABS.published ? 'published' : 'draft';
    const showAddButton = selectedTab === TABS.published;

    return (
        <>
            <div className={styles.sectionHeader}>
                <div className={styles.titleContainer}>
                    <h1 className={styles.headerTitle}>
                        Overview/Boomer Items/{category.name}
                    </h1>
                    <img
                        className={styles.headerImage}
                        src={boomerItemsImage}
                        alt='Boomer Items'
                    />
                </div>
                <BackLink to='/boomerItems' text='Back' />
            </div>

            <Tabs className={styles.tabsContainer}>
                <Tab
                    selected={selectedTab === TABS.published}
                    onClick={() => { setSelectedTab(TABS.published); setOpenMenuId(null); }}
                >
                    Published
                </Tab>
                <Tab
                    selected={selectedTab === TABS.draft}
                    onClick={() => { setSelectedTab(TABS.draft); setOpenMenuId(null); }}
                >
                    Draft
                </Tab>
                <Tab
                    selected={selectedTab === TABS.archived}
                    onClick={() => { setSelectedTab(TABS.archived); setOpenMenuId(null); }}
                >
                    Archived
                </Tab>
            </Tabs>

            {actionError && <p className={styles.statusMessage} role='alert'>{actionError}</p>}
            {itemsLoading && <Loader />}
            {itemsError && (
                <p className={styles.statusMessage}>{itemsError.message}</p>
            )}
            {!itemsLoading &&
                !itemsError &&
                visibleItems.length === 0 &&
                !showAddButton && (
                    <p className={styles.statusMessage}>
                        No {selectedTabName} {category.name.toLowerCase()} found.
                    </p>
                )}
            {!itemsLoading &&
                !itemsError &&
                (showAddButton || visibleItems.length > 0) && (
                <div className={styles.itemsWrapper}>
                    {showAddButton && (
                        <Link
                            to={`/boomerItems/${category.key}/new`}
                            className={`${styles.itemCard} ${styles.addItemCard}`}
                            aria-label={`Add ${category.itemName}`}
                        >
                            <img
                                className={styles.addItemIcon}
                                src={plusCircled}
                                alt=''
                            />
                        </Link>
                    )}
                    {visibleItems.map((item, index) => {
                        const imageUrl = getItemImage(item);
                        const itemLabel =
                            item.name ||
                            `${category.itemName} ${item.id || item.xmlId || index + 1}`;

                        return (
                            <div
                                key={`${category.key}-${item.id || item.xmlId || index}`}
                                className={`${styles.itemCard} dropdownHolder`}
                            >
                                <div className={categoryStyles.burgerMenuWrapper} ref={openMenuId === item.id ? menuRef : null}>
                                        <button type='button' className='dropdownMenuItem' aria-label={`Options for ${itemLabel}`} aria-expanded={openMenuId === item.id} onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}>
                                            <Menu size='24' />
                                        </button>
                                        {openMenuId === item.id && (
                                            <ul className='dropdownMenu dropdownMenuRight'>
                                                {showingArchived ?
                                                    <li><button type='button' className='dropdownMenuItem' disabled={archiving || deleting} onClick={() => onArchiveItem(item.id, false)}>{archiving ? 'Unarchiving...' : 'Unarchive'}</button></li> : selectedTab === TABS.draft ?
                                                    <li><Link className='dropdownMenuItem' to={`/boomerItems/${category.key}/${encodeURIComponent(item.id)}/edit`} onClick={() => setOpenMenuId(null)}>Edit</Link></li> :
                                                    <li><button type='button' className='dropdownMenuItem' disabled={deleting || archiving} onClick={() => onDeleteItem(item.id)}>{deleting ? 'Deleting...' : 'Delete'}</button></li>
                                                }
                                                {selectedTab === TABS.published && <li><button type='button' className='dropdownMenuItem' disabled={archiving || deleting} onClick={() => onArchiveItem(item.id, true)}>{archiving ? 'Archiving...' : 'Archive'}</button></li>}
                                            </ul>
                                        )}
                                    </div>
                                {imageUrl ? (
                                    <img
                                        className={styles.itemImage}
                                        src={imageUrl}
                                        alt={itemLabel}
                                    />
                                ) : (
                                    <span className={styles.imageUnavailable}>
                                        Image unavailable
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

export default BoomerCategoryItems;
